import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { headerContent } from './Header.content';
import styles from './Header.module.sass';
import logo from './assets/logo.svg';
import { IconPhone } from './assets/iconPhone';
import { IconMenu } from './assets/iconMenu';

const PIN_START_THRESHOLD = 120;
const PIN_END_THRESHOLD = 24;
const SCROLL_DELTA = 20;

export function Header() {
  const headerRef = useRef(null);
  const lastScrollY = useRef(0);
  const pinnedRef = useRef(false);

  const [pinned, setPinned] = useState(false);
  const [pinTransition, setPinTransition] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [shellHeight, setShellHeight] = useState(undefined);
  const [showLayoutsMenu, setShowLayoutsMenu] = useState(false);

  const syncShellHeight = useCallback(() => {
    const h = headerRef.current?.offsetHeight;
    if (!h) return;
    setShellHeight((prev) => (prev === h ? prev : h));
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollY.current;
      lastScrollY.current = y;

      if (!pinnedRef.current && y >= PIN_START_THRESHOLD) {
        pinnedRef.current = true;
        const h = headerRef.current?.offsetHeight;
        if (h) setShellHeight(h);
        setPinned(true);
        setHidden(false);
        return;
      }

      if (pinnedRef.current && y <= PIN_END_THRESHOLD) {
        pinnedRef.current = false;
        setPinned(false);
        setHidden(false);
        return;
      }

      if (!pinnedRef.current) return;

      if (delta > SCROLL_DELTA) setHidden(true);
      else if (delta < -SCROLL_DELTA) setHidden(false);
    };

    lastScrollY.current = window.scrollY;
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!pinned) {
      setPinTransition(false);
      return;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPinTransition(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [pinned]);

  useLayoutEffect(() => {
    if (!pinned) return;
    syncShellHeight();
  }, [pinned, syncShellHeight]);

  useEffect(() => {
    if (!pinned || !headerRef.current) return;
    const ro = new ResizeObserver(() => syncShellHeight());
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, [pinned, syncShellHeight]);

  return (
    <div
      className={styles.headerShell}
      style={shellHeight != null ? { height: shellHeight } : undefined}
    >
      <header
        ref={headerRef}
        className={`${styles.header} ${pinned ? styles.header_pinned : ''} ${pinned && pinTransition ? styles.header_pinTransition : ''} ${pinned && hidden ? styles.header_hidden : ''}`}
      >
        <Container className={styles['header__inner']}>
          <div className={styles['header__innerLeft']}>
            <div className={styles['header__menu']}>
              {headerContent.menu}
              <IconMenu className={styles['header__menu-icon-svg']} />
            </div>
            <nav className={styles['header__nav']} aria-label="Primary">
              {headerContent.navigation.map((item) => (
                <a
                  key={item.href + item.label}
                  className={styles['header__link']}
                  href={item.href}
                  {...(item.label.toLowerCase() === 'о проекте'
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          <div className={styles['header__innerRight']}>
            <Button
              className={styles['header__button']}
              href={headerContent.buttonHref}
              label={headerContent.buttonLabel}
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
            />
            <div className={`${styles['header__burger']}`}>
              <span></span>
              <span></span>
            </div>
          </div>

          <a className={styles['header__logo']} href="#top">
            <img src={logo} alt="Причал" />
          </a>

          <div className={styles['header__actions']}>
            <div
              className={styles['header__link-layouts-wrapper']}
              onMouseEnter={() => setShowLayoutsMenu(true)}
              onMouseLeave={() => setShowLayoutsMenu(false)}
            >
              {!showLayoutsMenu && (
                <a className={styles['header__link-layouts']} href={headerContent.cta.href}>
                  {headerContent.cta.label}
                </a>
              )}

              {showLayoutsMenu && (
                <div className={styles['header__layouts-menu']}>
                  {headerContent.layoutsMenu.map((item, index) => (
                    <React.Fragment key={item.label}>
                      {index > 0 && <div className={styles['header__layouts-divider']} />}
                      <a
                        href={item.href}
                        className={styles['header__layouts-link']}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShowLayoutsMenu(false)}
                      >
                        {item.label}
                      </a>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <a className={styles['header__phone']} href={headerContent.phone.href}>
              {headerContent.phone.label}
            </a>
            <a
              className={styles['header__phone-icon']}
              href={headerContent.phone.href}
              aria-label="Позвонить"
            >
              <IconPhone className={styles['header__phone-icon-svg']} />
            </a>
          </div>
        </Container>
      </header>
    </div>
  );
}
